import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Modal, DeleteModal } from '@app/components/shared/Modal';
import { Snackbar, useSnackbar } from '@app/components/shared/Snackbar';
import { AdminHeader } from '@app/components/shared/AdminHeader';
import { getMinDate, isValidFutureDate, MIN_DATE_WEEKS_IN_FUTURE } from '../../../utils/dateConstraints';

interface Event {
  id: number;
  occasion: string;
  title: string;
  event_date: string;
  budget_min: number;
  budget_max: number;
  comments: string;
  publicToken?: string | null;
  publicTokenExpires?: string | null;
  publicTokenCreated?: string | null;
}

interface Recipient {
  id: number;
  name: string;
}

interface Gift {
  id: number;
  title: string;
  description: string;
  url: string;
  price: string;
  imageUrl?: string | null;
  imageData?: string | null; // Base64 for local preview or buffer data
  imageFilename?: string | null;
  imageMimetype?: string | null;
  recipientId: number;
}

interface EventDetailsProps {
  onLogout: () => void;
}

export function EventDetails({ onLogout }: EventDetailsProps) {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const isNewEvent = eventId === 'new';
  
  const [event, setEvent] = useState<Event | null>(isNewEvent ? {
    id: 0,
    occasion: '',
    title: '',
    event_date: new Date().toISOString().split('T')[0],
    budget_min: 0,
    budget_max: 0,
    comments: ''
  } : null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [gifts, setGifts] = useState<Gift[]>([]);
  const [loading, setLoading] = useState(isNewEvent ? false : true);
  const [error, setError] = useState('');
  const [editingEvent, setEditingEvent] = useState(isNewEvent);
  const [showDeleteModal, setShowDeleteModal] = useState<{ show: boolean; type?: 'recipient' | 'gift'; id?: number }>({ show: false });
  const [showAddRecipientModal, setShowAddRecipientModal] = useState(false);
  const [newRecipient, setNewRecipient] = useState({ name: '' });
  const [currentGiftRecipient, setCurrentGiftRecipient] = useState<number | null>(null);
  const [showGiftForm, setShowGiftForm] = useState(false);
  const [newGift, setNewGift] = useState({ title: '', description: '', url: '', price: '', imageUrl: '' });
  const [giftImageFile, setGiftImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUploadMode, setImageUploadMode] = useState<'url' | 'file'>('url');
  const [editingGiftImage, setEditingGiftImage] = useState<number | null>(null);
  const [giftImageFiles, setGiftImageFiles] = useState<{[key: number]: File}>({});
  const [giftImagePreviews, setGiftImagePreviews] = useState<{[key: number]: string}>({});
  const [giftImageModes, setGiftImageModes] = useState<{[key: number]: 'url' | 'file'}>({});
  const [giftImageUrls, setGiftImageUrls] = useState<{[key: number]: string}>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [originalEvent, setOriginalEvent] = useState<Event | null>(null);
  const [originalRecipients, setOriginalRecipients] = useState<Recipient[]>([]);
  const [originalGifts, setOriginalGifts] = useState<Gift[]>([]);
  const [deletedRecipients, setDeletedRecipients] = useState<number[]>([]);
  const [deletedGifts, setDeletedGifts] = useState<number[]>([]);
  const [editingRecipients, setEditingRecipients] = useState<Set<number>>(new Set());
  const [sendingGiftList, setSendingGiftList] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const allowNavigationRef = useRef(false);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const customerId = searchParams.get('customerId');

  // Add beforeunload warning when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        // Only call preventDefault is sufficient in modern browsers
      }
    };

    // Push a dummy state when there are changes to detect back navigation
    if (hasChanges) {
      window.history.pushState({ hasUnsavedChanges: true }, '');
    }

    const handlePopState = (e: PopStateEvent) => {
      if (hasChanges && !allowNavigationRef.current) {
        const shouldLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
        if (!shouldLeave) {
          // Stay on the page by pushing state again
          window.history.pushState({ hasUnsavedChanges: true }, '');
        } else {
          // User chose to leave - allow navigation by going back
          allowNavigationRef.current = true;
          window.history.back();
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasChanges]);

  // Helper function to normalize null/undefined/empty values for comparison
  const normalizeValue = (value: any) => {
    return value === null || value === undefined || value === '' ? null : value;
  };

  // Check if there are any changes to enable/disable Save button
  const checkForChanges = () => {
    if (isNewEvent) {
      // For new events, check if user has made actual changes (not just default values)
      const defaultDate = new Date().toISOString().split('T')[0];
      const hasActualChanges = event?.title?.trim() || 
                               (event?.event_date && event.event_date !== defaultDate) || 
                               recipients.length > 0 || 
                               gifts.length > 0;
      setHasChanges(!!hasActualChanges);
      return;
    }

    // For existing events, compare with originals
    const eventChanged = originalEvent && (
      event?.title !== originalEvent.title ||
      event?.event_date !== originalEvent.event_date
    );

    const recipientsChanged = originalRecipients.length !== recipients.filter(r => !deletedRecipients.includes(r.id)).length ||
      recipients.some(r => {
        const original = originalRecipients.find(or => or.id === r.id);
        return !original || original.name !== r.name;
      });

    const giftsChanged = originalGifts.length !== gifts.filter(g => !deletedGifts.includes(g.id)).length ||
      gifts.some(g => {
        const original = originalGifts.find(og => og.id === g.id);
        return !original || 
          original.title !== g.title ||
          original.description !== g.description ||
          original.url !== g.url ||
          original.price !== g.price ||
          normalizeValue(original.imageUrl) !== normalizeValue(g.imageUrl) ||
          normalizeValue(original.imageData) !== normalizeValue(g.imageData) ||
          normalizeValue(original.imageFilename) !== normalizeValue(g.imageFilename) ||
          normalizeValue(original.imageMimetype) !== normalizeValue(g.imageMimetype);
      });

    const hasDeletedItems = deletedRecipients.length > 0 || deletedGifts.length > 0;
    const hasNewItems = recipients.some(r => r.id < 0) || gifts.some(g => g.id < 0); // Negative IDs for new items

    const hasChanges = !!(eventChanged || recipientsChanged || giftsChanged || hasDeletedItems || hasNewItems);
    console.log('Change detection:', { eventChanged, recipientsChanged, giftsChanged, hasDeletedItems, hasNewItems, hasChanges });
    setHasChanges(hasChanges);
  };

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      
      if (!eventId) {
        setLoading(false);
        return;
      }
      
      if (isNewEvent) {
        // Initialize new event with default values
        setEvent({
          id: 0,
          occasion: '',
          title: '',
          event_date: new Date().toISOString().split('T')[0],
          budget_min: 0,
          budget_max: 0,
          comments: ''
        });
        setRecipients([]);
        setGifts([]);
        setEditingEvent(true);
        setLoading(false);
        return;
      }
      
      const response = await fetch(`/api/admin/events/${eventId}`, {
        credentials: 'include',
      });

      if (response.status === 401) {
        onLogout();
        navigate('/login');
        return;
      }

      const result = await response.json();
      if (result.success) {
        setEvent(result.data.event);
        setRecipients(result.data.recipients);
        setGifts(result.data.gifts);
        // Store originals for change tracking
        setOriginalEvent(result.data.event);
        setOriginalRecipients(result.data.recipients);
        setOriginalGifts(result.data.gifts);
        setHasChanges(false);
        
        // Reset all change tracking state variables
        setDeletedRecipients([]);
        setDeletedGifts([]);
        setEditingRecipients(new Set());
        setSavingEvent(false);
        
        // Reset image-related state variables
        setGiftImageFiles({});
        setGiftImagePreviews({});
        setGiftImageModes({});
        setGiftImageUrls({});
        setEditingGiftImage(null);
        
        // Reset navigation prevention
        allowNavigationRef.current = false;
      } else {
        showSnackbar(result.error || 'Failed to fetch event details', 'error');
      }
    } catch (err) {
      showSnackbar('Network error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEventDetails();
  }, [eventId]);

  useEffect(() => {
    checkForChanges();
  }, [event, recipients, gifts, deletedRecipients, deletedGifts]);

  const getGiftListStatusBadge = (event: Event) => {
    if (event.publicToken && event.publicTokenCreated) {
      return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">Gift List Sent</span>;
    } else {
      return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">Gift List not created</span>;
    }
  };

  const handleSaveEvent = async () => {
    if (!event || isNewEvent) return;

    try {
      const response = await fetch(`/api/admin/events/${event.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          occasion: event.occasion,
          title: event.title,
          event_date: event.event_date,
          budget_min: event.budget_min,
          budget_max: event.budget_max,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setEditingEvent(false);
        showSnackbar('Event updated successfully!', 'success');
      } else {
        showSnackbar(result.error || 'Failed to update event', 'error');
      }
    } catch (err) {
      showSnackbar('Network error occurred', 'error');
    }
  };

  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type - only allow common image formats
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        showSnackbar('Please select a valid image file (JPEG, PNG, GIF, or WebP)', 'error');
        return;
      }
      
      // Validate file size (max 4MB)
      if (file.size > 4 * 1024 * 1024) {
        showSnackbar('Image size must be less than 4MB', 'error');
        return;
      }
      
      setGiftImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUrlChange = (url: string) => {
    setNewGift({ ...newGift, imageUrl: url });
    if (url) {
      setImagePreview(url);
    } else {
      setImagePreview(null);
    }
  };

  const clearImage = () => {
    setGiftImageFile(null);
    setImagePreview(null);
    setNewGift({ ...newGift, imageUrl: '' });
  };

  const handleGiftImageFileChange = (giftId: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type - only allow common image formats
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        showSnackbar('Please select a valid image file (JPEG, PNG, GIF, or WebP)', 'error');
        return;
      }
      
      // Validate file size (max 4MB)
      if (file.size > 4 * 1024 * 1024) {
        showSnackbar('Image size must be less than 4MB', 'error');
        return;
      }
      
      setGiftImageFiles({ ...giftImageFiles, [giftId]: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setGiftImagePreviews({ 
          ...giftImagePreviews, 
          [giftId]: e.target?.result as string 
        });
        // Update gift with image data for saving - THIS IS CRITICAL
        setGifts(prevGifts => prevGifts.map(gift => 
          gift.id === giftId ? {
            ...gift,
            imageData: e.target?.result as string,
            imageFilename: file.name,
            imageMimetype: file.type,
            imageUrl: '' // Clear URL when file is set
          } : gift
        ));
        setEditingGiftImage(null); // Close editing mode after successful upload
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGiftImageUrlChange = (giftId: number, url: string) => {
    // Update local URL state for immediate input feedback
    setGiftImageUrls({ ...giftImageUrls, [giftId]: url });
    
    // Update the gift directly in the gifts array - THIS IS CRITICAL
    setGifts(prevGifts => prevGifts.map(gift => 
      gift.id === giftId ? {
        ...gift,
        imageUrl: url,
        imageData: '',
        imageFilename: '',
        imageMimetype: ''
      } : gift
    ));
    
    if (url) {
      setGiftImagePreviews({ ...giftImagePreviews, [giftId]: url });
    } else {
      const newPreviews = { ...giftImagePreviews };
      delete newPreviews[giftId];
      setGiftImagePreviews(newPreviews);
    }
  };

  const clearGiftImage = (giftId: number) => {
    console.log('Clearing image for gift:', giftId);
    const newFiles = { ...giftImageFiles };
    const newPreviews = { ...giftImagePreviews };
    const newModes = { ...giftImageModes };
    const newUrls = { ...giftImageUrls };
    
    delete newFiles[giftId];
    delete newPreviews[giftId];
    delete newModes[giftId];
    delete newUrls[giftId];
    
    setGiftImageFiles(newFiles);
    setGiftImagePreviews(newPreviews);
    setGiftImageModes(newModes);
    setGiftImageUrls(newUrls);
    setEditingGiftImage(null); // Close editing mode
    
    // Clear all image fields directly in the gifts array - THIS IS CRITICAL
    setGifts(prevGifts => prevGifts.map(gift => 
      gift.id === giftId ? {
        ...gift,
        imageUrl: '',
        imageData: '',
        imageFilename: '',
        imageMimetype: ''
      } : gift
    ));
  };

  // Clear gift form data
  const clearGiftForm = () => {
    setShowGiftForm(false);
    setCurrentGiftRecipient(null);
    setNewGift({ title: '', description: '', url: '', price: '', imageUrl: '' });
    setGiftImageFile(null);
    setImagePreview(null);
    setImageUploadMode('url');
  };

  // Check if URL is Amazon product
  const isAmazonUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.includes('amazon.') || urlObj.hostname.includes('amzn.');
    } catch {
      return false;
    }
  };

  // Scrape Amazon product data
  const scrapeAmazonProduct = async (url: string) => {
    try {
      const response = await fetch('/api/scrape/amazon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ url })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Pre-fill the form fields with scraped data
          setNewGift(prev => ({
            ...prev,
            title: result.data.title || prev.title,
            description: result.data.description || prev.description,
            price: result.data.price || prev.price,
            imageUrl: imageUploadMode === 'url' ? (result.data.image || prev.imageUrl) : prev.imageUrl
          }));
          
          // Set image preview if in URL mode and image was found
          if (imageUploadMode === 'url' && result.data.image) {
            setImagePreview(result.data.image);
          }
          
          showSnackbar('Product details imported from Amazon!', 'success');
        }
      }
    } catch (error) {
      console.log('Amazon scraping not available:', error);
      // Silently fail - the user can still fill the form manually
    }
  };

  // Handle URL change with Amazon scraping
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setNewGift({ ...newGift, url });
    
    // If it's an Amazon URL, attempt to scrape product data
    if (url && isAmazonUrl(url)) {
      scrapeAmazonProduct(url);
    }
  };

  const handleAddGift = async () => {
    // Validation
    if (!newGift.title.trim()) {
      showSnackbar('Gift title is required', 'error');
      return;
    }
    if (!newGift.url.trim()) {
      showSnackbar('Gift URL is required', 'error');
      return;
    }
    if (!currentGiftRecipient) {
      showSnackbar('Please select a recipient for this gift', 'error');
      return;
    }
    
    // URL validation
    try {
      new URL(newGift.url);
    } catch {
      showSnackbar('Please enter a valid URL', 'error');
      return;
    }

    // Handle image data
    let giftImageData: string | null = null;
    if (giftImageFile) {
      // Convert file to base64 for local storage
      const reader = new FileReader();
      giftImageData = await new Promise<string>((resolve) => {
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(giftImageFile);
      });
    }

    // Add gift to local state with negative ID (indicates new item)
    const newGiftData: Gift = {
      id: -Date.now(), // Negative ID for new items
      title: newGift.title,
      description: newGift.description,
      url: newGift.url,
      price: newGift.price,
      recipientId: currentGiftRecipient,
      imageUrl: imageUploadMode === 'url' ? newGift.imageUrl : null,
      imageData: giftImageData, // Store base64 for preview
      imageFilename: giftImageFile?.name || null,
      imageMimetype: giftImageFile?.type || null
    };
    
    setGifts([...gifts, newGiftData]);
    clearGiftForm();
    checkForChanges();
    showSnackbar('Gift added locally. Click "Save Event" to save to database.', 'success');
  };


  const handleAddRecipient = () => {
    if (!newRecipient.name.trim()) {
      showSnackbar('Recipient name is required', 'error');
      return;
    }

    // Add recipient to local state with negative ID (indicates new item)
    const newRecipientData = {
      id: -Date.now(), // Negative ID for new items
      name: newRecipient.name.trim()
    };
    
    setRecipients([...recipients, newRecipientData]);
    setShowAddRecipientModal(false);
    setNewRecipient({ name: '' });
    checkForChanges();
    showSnackbar('Recipient added locally. Click "Save Event" to save to database.', 'success');
  };

  const handleDeleteRecipient = (recipientId: number) => {
    if (recipientId > 0) {
      // Existing recipient - mark for deletion
      setDeletedRecipients([...deletedRecipients, recipientId]);
      // Also mark associated gifts for deletion
      const recipientGifts = gifts.filter(g => g.recipientId === recipientId);
      setDeletedGifts([...deletedGifts, ...recipientGifts.map(g => g.id).filter(id => id > 0)]);
    }
    // Remove from current recipients (both new and existing)
    setRecipients(recipients.filter(r => r.id !== recipientId));
    // Remove associated gifts from current gifts
    setGifts(gifts.filter(g => g.recipientId !== recipientId));
    setShowDeleteModal({ show: false });
    checkForChanges();
    showSnackbar('Recipient deleted locally. Click "Save Event" to save to database.', 'success');
  };

  const handleDeleteGift = (giftId: number) => {
    if (giftId > 0) {
      // Existing gift - mark for deletion
      setDeletedGifts([...deletedGifts, giftId]);
    }
    // Remove from current gifts (both new and existing)
    setGifts(gifts.filter(g => g.id !== giftId));
    setShowDeleteModal({ show: false });
    checkForChanges();
    showSnackbar('Gift deleted locally. Click "Save Event" to save to database.', 'success');
  };

  const handleUpdateRecipientName = (recipientId: number, newName: string) => {
    setRecipients(recipients.map(r => 
      r.id === recipientId ? { ...r, name: newName } : r
    ));
    checkForChanges();
  };


  const handleGiftFieldChange = (giftId: number, field: string, value: string) => {
    setGifts(gifts.map(gift => 
      gift.id === giftId ? { ...gift, [field]: value } : gift
    ));
    checkForChanges();
  };

  const handleSaveCompleteEvent = async () => {
    if (!event || !hasChanges || savingEvent) return;
    
    setSavingEvent(true);
    
    // Validation
    if (!event.title?.trim()) {
      showSnackbar('Event title is required', 'error');
      setSavingEvent(false);
      return;
    }
    if (!event.event_date) {
      showSnackbar('Event date is required', 'error');
      setSavingEvent(false);
      return;
    }

    try {
      if (isNewEvent) {
        // Create new event with all data

        const response = await fetch('/api/admin/events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            account_id: customerId,
            occasion: event.title || 'Custom Event',
            title: event.title,
            event_date: event.event_date,
            budget_min: event.budget_min || 0,
            budget_max: event.budget_max || 0,
            comments: event.comments || '',
            recipients: recipients,
            gifts: gifts
          }),
        });

        const result = await response.json();
        if (result.success) {
          showSnackbar('Event created successfully!', 'success');
          // Reset editing state before navigation
          setEditingEvent(false);
          setSavingEvent(false);
          setHasChanges(false);
          allowNavigationRef.current = true; // Allow navigation without warning
          
          // Navigate to the newly created event's detail page
          const newEventId = result.data?.id || result.data?.event?.id;
          if (newEventId) {
            navigate(`/admin/events/${newEventId}`);
          } else {
            // Fallback to events list if we don't get the new event ID
            navigate(`/admin/customers/${customerId}/events`);
          }
        } else {
          showSnackbar(result.error || 'Failed to create event', 'error');
          setSavingEvent(false);
        }
      } else {
        // Update existing event with CRUD operations
        const response = await fetch(`/api/admin/events/${event.id}/bulk-update`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            event: {
              title: event.title,
              occasion: event.title || 'Custom Event',
              event_date: event.event_date,
              budget_min: event.budget_min || 0,
              budget_max: event.budget_max || 0,
              comments: event.comments || ''
            },
            recipients: {
              new: recipients.filter(r => r.id < 0),
              updated: recipients.filter(r => r.id > 0),
              deleted: deletedRecipients
            },
            gifts: {
              new: gifts.filter(g => g.id < 0),
              updated: gifts.filter(g => g.id > 0),
              deleted: deletedGifts
            }
          }),
        });

        const result = await response.json();
        if (result.success) {
          showSnackbar('Event updated successfully!', 'success');
          // Refresh the data
          fetchEventDetails();
        } else {
          showSnackbar(result.error || 'Failed to update event', 'error');
          setSavingEvent(false);
        }
      }
    } catch (err) {
      showSnackbar('Network error occurred', 'error');
      setSavingEvent(false);
    }
  };

  const handleSendGiftList = async () => {
    if (!event || !eventId || isNewEvent) return;
    
    setSendingGiftList(true);
    
    try {
      const response = await fetch(`/api/admin/events/${eventId}/send-gift-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const result = await response.json();
      
      if (result.success) {
        showSnackbar('Gift list email sent successfully!', 'success');
        // Refresh the page to show updated token status
        window.location.reload();
      } else {
        showSnackbar(result.error || 'Failed to send gift list email', 'error');
      }
    } catch (err) {
      showSnackbar('Network error occurred', 'error');
    } finally {
      setSendingGiftList(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading event details...</div>
      </div>
    );
  }

  
  if (!event && !isNewEvent) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-red-600">Event not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader onLogout={onLogout} />

      <main className="max-w-4xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => {
            if (hasChanges) {
              const shouldLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
              if (shouldLeave) {
                // Allow navigation by setting the ref flag
                allowNavigationRef.current = true;
                if (customerId) {
                  navigate(`/admin/customers/${customerId}/events`);
                } else {
                  navigate('/admin');
                }
              }
            } else {
              // Allow navigation by setting the ref flag
              allowNavigationRef.current = true;
              if (customerId) {
                navigate(`/admin/customers/${customerId}/events`);
              } else {
                navigate('/admin');
              }
            }
          }}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-6 cursor-pointer"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Events
        </button>


        <div className="bg-white shadow-sm rounded-lg">
          {/* Event Details Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold text-gray-900">{isNewEvent ? 'Create New Event:' : 'Event Details:'}</h1>
              {event && !isNewEvent && getGiftListStatusBadge(event)}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Title:</label>
                <input
                  type="text"
                  value={event?.title || ''}
                  onChange={(e) => setEvent({ ...(event || { id: 0, occasion: '', title: '', event_date: '', budget_min: 0, budget_max: 0, comments: '' }), title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Birthday Party"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date:</label>
                <input
                  type="date"
                  value={event?.event_date ? new Date(event.event_date).toISOString().split('T')[0] : ''}
                  min={getMinDate()}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    setEvent({ ...(event || { id: 0, occasion: '', title: '', event_date: '', budget_min: 0, budget_max: 0, comments: '' }), event_date: selectedDate });
                  }}
                  onBlur={(e) => {
                    // Only validate complete dates on blur for Safari iOS
                    const selectedDate = e.target.value;
                    if (selectedDate && !isValidFutureDate(selectedDate)) {
                      alert(`Please select a date at least ${MIN_DATE_WEEKS_IN_FUTURE} weeks in the future.`);
                      e.target.value = '';
                      setEvent({ ...(event || { id: 0, occasion: '', title: '', event_date: '', budget_min: 0, budget_max: 0, comments: '' }), event_date: '' });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Gift List Link */}
            {event && !isNewEvent && event.publicToken && event.publicTokenCreated && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-gray-700 mb-2">Gift List Link:</p>
                <div className="flex items-center space-x-2">
                  <a
                    href={`${window.location.origin}/gift-list/${event.publicToken}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-blue-50 transition-colors break-all"
                  >
                    {`${window.location.origin}/gift-list/${event.publicToken}`}
                  </a>
                  <button
                    onClick={() => navigator.clipboard.writeText(`${window.location.origin}/gift-list/${event.publicToken}`)}
                    className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
            
            {!isNewEvent && editingEvent && (
              <div className="mt-4 flex space-x-2">
                <button
                  onClick={handleSaveEvent}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 cursor-pointer"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditingEvent(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Recipients Section */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Recipients:</h2>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">{recipients.length} Recipients added</span>
                <button 
                  onClick={() => setShowAddRecipientModal(true)}
                  className="bg-black text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  + ADD A NEW RECIPIENT
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {recipients
                .sort((a, b) => {
                  // Existing recipients (positive IDs) first, then new recipients (negative IDs)
                  if (a.id > 0 && b.id < 0) return -1;
                  if (a.id < 0 && b.id > 0) return 1;
                  // Within the same type, maintain original order
                  if (a.id > 0 && b.id > 0) return a.id - b.id;
                  // For negative IDs, reverse order so latest additions appear last
                  return b.id - a.id;
                })
                .map((recipient, index) => (
                <div key={recipient.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">Recipient {index + 1}</h3>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setShowDeleteModal({ show: true, type: 'recipient', id: recipient.id })}
                        className="text-gray-600 hover:text-red-600 p-1 cursor-pointer"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                      <span className="text-xs text-gray-500">Delete Recipient</span>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Recipient name</label>
                      <input
                        type="text"
                        value={recipient.name}
                        onChange={(e) => handleUpdateRecipientName(recipient.id, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Jean"
                      />
                    </div>
                  </div>

                  {/* Gifts for this recipient */}
                  <div className="mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {gifts.filter(gift => gift.recipientId === recipient.id).map((gift, giftIndex) => (
                        <div key={gift.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium text-gray-900">Gift {String.fromCharCode(65 + giftIndex)}</h4>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => setShowDeleteModal({ show: true, type: 'gift', id: gift.id })}
                                className="text-gray-600 hover:text-red-600 p-1 cursor-pointer"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                              <span className="text-xs text-gray-500">Delete gift</span>
                            </div>
                          </div>

                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Gift title:</label>
                              <input
                                type="text"
                                value={gift.title}
                                onChange={(e) => handleGiftFieldChange(gift.id, 'title', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="Cozy sweater with stars"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700">Gift URL:</label>
                              <input
                                type="url"
                                value={gift.url}
                                onChange={(e) => handleGiftFieldChange(gift.id, 'url', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="https://example.com/product123"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700">Gift Image:</label>
                              
                              {editingGiftImage === gift.id ? (
                                // Image editing mode
                                <div className="space-y-3">
                                  {/* Mode selector */}
                                  <div>
                                    <label style={{display: 'inline-block', marginRight: '20px'}}>
                                      <input
                                        type="radio"
                                        name={`imageMode_${gift.id}`}
                                        value="url"
                                        checked={(giftImageModes[gift.id] || 'url') === 'url'}
                                        onChange={() => setGiftImageModes({ ...giftImageModes, [gift.id]: 'url' })}
                                      />
                                      Image URL
                                    </label>
                                    <label style={{display: 'inline-block'}}>
                                      <input
                                        type="radio"
                                        name={`imageMode_${gift.id}`}
                                        value="file"
                                        checked={(giftImageModes[gift.id] || 'url') === 'file'}
                                        onChange={() => setGiftImageModes({ ...giftImageModes, [gift.id]: 'file' })}
                                      />
                                      Upload File
                                    </label>
                                  </div>

                                  {/* URL input */}
                                  {(giftImageModes[gift.id] || 'url') === 'url' && (
                                    <input
                                      type="url"
                                      value={giftImageUrls[gift.id] || gift.imageUrl || ''}
                                      onChange={(e) => handleGiftImageUrlChange(gift.id, e.target.value)}
                                      onBlur={() => setEditingGiftImage(null)} // Close editing mode when user finishes typing
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                      placeholder="https://example.com/image.jpg"
                                    />
                                  )}

                                  {/* File upload */}
                                  {(giftImageModes[gift.id] || 'url') === 'file' && (
                                    <div>
                                      <input
                                        type="file"
                                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                                        onChange={(e) => handleGiftImageFileChange(gift.id, e)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                      />
                                      <p className="text-xs text-gray-500 mt-1">
                                        Supported formats: JPEG, PNG, GIF, WebP (max 4MB)
                                      </p>
                                    </div>
                                  )}

                                  {/* Image preview */}
                                  {(() => {
                                    const previewSrc = giftImagePreviews[gift.id] || 
                                      (gift.imageData && gift.imageData.startsWith('data:') ? gift.imageData :
                                       (gift.id > 0 && gift.imageFilename ? `/api/admin/gifts/${gift.id}/image` :
                                        gift.imageUrl));
                                    
                                    if (previewSrc) {
                                      return (
                                        <div className="mt-2">
                                          <div className="w-24 h-24 border border-gray-300 rounded-lg overflow-hidden">
                                            <img src={previewSrc} alt={gift.title} className="w-full h-full object-cover" />
                                          </div>
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}

                                  {/* Action buttons */}
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={() => clearGiftImage(gift.id)}
                                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                                    >
                                      Remove Image
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                // Display mode
                                <div>
                                  <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center mb-2">
                                    {(() => {
                                      // For local preview (updated gifts with base64 data)
                                      if (giftImagePreviews[gift.id]) {
                                        return <img src={giftImagePreviews[gift.id]} alt={gift.title} className="w-16 h-16 object-cover rounded-lg" />;
                                      }
                                      // For local preview (new gifts with base64 data)
                                      if (gift.imageData && gift.imageData.startsWith('data:')) {
                                        return <img src={gift.imageData} alt={gift.title} className="w-16 h-16 object-cover rounded-lg" />;
                                      }
                                      // For existing gifts with stored images
                                      if (gift.id > 0 && gift.imageFilename) {
                                        return <img src={`/api/admin/gifts/${gift.id}/image`} alt={gift.title} className="w-16 h-16 object-cover rounded-lg" />;
                                      }
                                      // For URL-based images
                                      if (gift.imageUrl) {
                                        return <img src={gift.imageUrl} alt={gift.title} className="w-16 h-16 object-cover rounded-lg" />;
                                      }
                                      // Default placeholder
                                      return (
                                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                      );
                                    })()}
                                  </div>
                                  <button
                                    onClick={() => {
                                      setEditingGiftImage(gift.id);
                                      // Initialize mode based on current state
                                      if (gift.imageUrl) {
                                        setGiftImageModes({ ...giftImageModes, [gift.id]: 'url' });
                                      } else if (gift.imageFilename) {
                                        setGiftImageModes({ ...giftImageModes, [gift.id]: 'file' });
                                      } else {
                                        setGiftImageModes({ ...giftImageModes, [gift.id]: 'url' });
                                      }
                                    }}
                                    className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                                  >
                                    {gift.imageUrl || gift.imageFilename ? 'Edit Image' : 'Add Image'}
                                  </button>
                                </div>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700">Gift description:</label>
                              <textarea
                                value={gift.description}
                                onChange={(e) => handleGiftFieldChange(gift.id, 'description', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                rows={3}
                                placeholder="Gift description"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700">Price:</label>
                              <input
                                type="text"
                                value={gift.price}
                                onChange={(e) => handleGiftFieldChange(gift.id, 'price', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                placeholder="$80 for standard version, $160 for custom color"
                              />
                            </div>
                            
                          </div>
                        </div>
                      ))}

                      {/* Add Gift Button */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex items-center justify-center min-h-[200px]">
                        <button
                          onClick={() => {
                            // Clear form data first, then set recipient and open modal
                            setNewGift({ title: '', description: '', url: '', price: '', imageUrl: '' });
                            setGiftImageFile(null);
                            setImagePreview(null);
                            setImageUploadMode('url');
                            setCurrentGiftRecipient(recipient.id);
                            setShowGiftForm(true);
                          }}
                          className="text-gray-500 hover:text-gray-700 text-center cursor-pointer"
                        >
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium">ADD GIFT</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Add New Recipient Button - positioned like in wireframe */}
            {recipients.length > 0 && <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <span className="text-sm text-gray-600">{recipients.length} Recipients added</span>
              <button 
                onClick={() => setShowAddRecipientModal(true)}
                className="bg-black text-white px-6 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer"
              >
                + ADD A NEW RECIPIENT
              </button>
            </div>}

            {/* Save Event and Send Gift List Buttons */}
            <div className="flex justify-center space-x-6 mt-8 pb-6">
              <button 
                onClick={handleSaveCompleteEvent}
                className={`border-2 border-black px-12 py-3 rounded-full font-medium transition-colors min-w-[200px] ${
                  hasChanges && !savingEvent
                    ? 'bg-white text-black hover:bg-gray-50 cursor-pointer' 
                    : 'bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed'
                }`}
                disabled={!hasChanges || savingEvent}
              >
                {savingEvent ? 'SAVING...' : 'SAVE EVENT'}
              </button>
              
              <button 
                onClick={handleSendGiftList}
                disabled={sendingGiftList || hasChanges}
                className={`px-12 py-3 rounded-full font-medium transition-colors min-w-[200px] ${
                  hasChanges 
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'bg-black text-white hover:bg-gray-800 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
              >
                {sendingGiftList ? 'SENDING...' : (event?.publicToken && event?.publicTokenCreated ? 'RE-SEND CLIENT GIFT LIST' : 'SEND CLIENT GIFT LIST')}
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Add New Recipient Modal */}
      <Modal 
        isOpen={showAddRecipientModal} 
        onClose={() => setShowAddRecipientModal(false)}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Recipient</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Recipient Name</label>
            <input
              type="text"
              value={newRecipient.name}
              onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="John Doe"
            />
          </div>
        </div>
        <div className="flex space-x-3 mt-6">
          <button
            onClick={handleAddRecipient}
            className="flex-1 bg-black text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer"
          >
            Add Recipient
          </button>
          <button
            onClick={() => {
              setShowAddRecipientModal(false);
              setNewRecipient({ name: '' });
            }}
            className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </Modal>

      {/* Gift Form Modal */}
      <Modal 
        isOpen={showGiftForm} 
        onClose={() => {
          clearGiftForm();
        }}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Gift</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">URL</label>
            <input
              type="url"
              value={newGift.url}
              onChange={handleUrlChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="https://example.com or Amazon product URL"
              maxLength={500}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Gift Title</label>
            <input
              type="text"
              value={newGift.title}
              onChange={(e) => setNewGift({ ...newGift, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Gift title"
              maxLength={255}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={newGift.description}
              onChange={(e) => setNewGift({ ...newGift, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              rows={3}
              placeholder="Gift description"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Price</label>
            <input
              type="text"
              value={newGift.price}
              onChange={(e) => setNewGift({ ...newGift, price: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="$99.99"
              maxLength={100}
            />
          </div>
          
          {/* Image Upload Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gift Image</label>
            
            {/* Image Upload Mode Toggle */}
            <div className="mb-3">
              <div>
                <label style={{display: 'inline-block', marginRight: '20px'}}>
                  <input
                    type="radio"
                    name="imageMode"
                    value="url"
                    checked={imageUploadMode === 'url'}
                    onChange={() => {
                      setImageUploadMode('url');
                      clearImage();
                    }}
                  />
                  Image URL
                </label>
                <label style={{display: 'inline-block'}}>
                  <input
                    type="radio"
                    name="imageMode"
                    value="file"
                    checked={imageUploadMode === 'file'}
                    onChange={() => {
                      setImageUploadMode('file');
                      setNewGift({ ...newGift, imageUrl: '' });
                    }}
                  />
                  Upload File
                </label>
              </div>
            </div>

            {/* URL Input */}
            {imageUploadMode === 'url' && (
              <input
                type="url"
                value={newGift.imageUrl}
                onChange={(e) => handleImageUrlChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="https://example.com/image.jpg"
                maxLength={500}
              />
            )}

            {/* File Upload */}
            {imageUploadMode === 'file' && (
              <div>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageFileChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supported formats: JPEG, PNG, GIF, WebP (max 4MB)
                </p>
              </div>
            )}

            {/* Image Preview */}
            {imagePreview && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Preview:</span>
                  <button
                    onClick={clearImage}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remove
                  </button>
                </div>
                <div className="w-32 h-32 border border-gray-300 rounded-lg overflow-hidden">
                  <img
                    src={imagePreview}
                    alt="Gift preview"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex space-x-3 mt-6">
          <button
            onClick={handleAddGift}
            className="flex-1 bg-black text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer"
          >
            Add Gift
          </button>
          <button
            onClick={() => {
              clearGiftForm();
            }}
            className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-700 transition-colors cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </Modal>

      {/* Delete Modal */}
      <DeleteModal
        isOpen={showDeleteModal.show}
        onClose={() => setShowDeleteModal({ show: false })}
        onConfirm={() => {
          if (showDeleteModal.type === 'gift' && showDeleteModal.id) {
            handleDeleteGift(showDeleteModal.id);
          } else if (showDeleteModal.type === 'recipient' && showDeleteModal.id) {
            handleDeleteRecipient(showDeleteModal.id);
          }
        }}
        title={`Do you want to delete this ${showDeleteModal.type === 'gift' ? 'Gift' : 'Recipient'}?`}
        message="Sed sagittis purus eu gravida. Venenatis est eros lacus elementum sodales orci. Fermentum turpis ut consequat ac. Eget sagittis amet sed."
      />

      {/* Snackbar */}
      <Snackbar
        message={snackbar.message}
        type={snackbar.type}
        isVisible={snackbar.isVisible}
        onClose={hideSnackbar}
      />
    </div>
  );
}